import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { ReturnActionKind, ReturnActionRequest, ReturnDetail } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import { actionLabel, mergeReturnTemplate, TemplateExtras, templateKeysFor, templateLabel, whatHappensNext } from "../../utils/returnsRules";

/** What every action panel receives: it builds the request body and hands it back on Confirm. */
export interface ReturnActionPanelProps {
  detail: ReturnDetail;
  onConfirm: (body: ReturnActionRequest) => void;
  onCancel: () => void;
  busy: boolean;
}

export interface ActionPanelProps {
  detail: ReturnDetail;
  onConfirm: (message: string) => void;
  onCancel: () => void;
  busy: boolean;
}

interface ActionPanelFrameProps extends ActionPanelProps {
  kind: ReturnActionKind;
  /** Defaults to the action's label. */
  title?: string;
  /** Inputs for the action (label choice, RMA number, bands…), shown above "What happens next". */
  children?: ReactNode;
  /** Defaults to "Confirm – send to eBay" (or just "Confirm" on a manual return). */
  confirmLabel?: string;
  canConfirm?: boolean;
  /** Values merged into the template on top of the return's own ({refund_amount} as the deduction changes). */
  templateExtras?: TemplateExtras;
  /** Under the message label: defaults to "· eBay comment field". */
  messageHint?: string;
  /** The message is optional on some actions (RMA, mark received). */
  messageRequired?: boolean;
  /** A template to start from even if the action doesn't normally offer it (own_tracking on a message). */
  initialTemplateKey?: string;
}

/**
 * The confirm panel every "What eBay allows now" action opens: inputs on the left with the
 * "What happens next on eBay" box, the templated message to the buyer on the right, and the
 * confirm / cancel row. Nothing is sent until Confirm.
 */
export default function ActionPanelFrame({
  kind,
  detail,
  title,
  children,
  confirmLabel,
  canConfirm = true,
  templateExtras,
  messageHint,
  messageRequired = true,
  initialTemplateKey,
  onConfirm,
  onCancel,
  busy,
}: ActionPanelFrameProps) {
  const { t } = useTranslation("returns");
  const templateKeys = useMemo(() => {
    const keys = templateKeysFor(kind);
    const all = initialTemplateKey ? [initialTemplateKey, ...keys.filter((key) => key !== initialTemplateKey)] : keys;
    return all.filter((key) => !!detail.templates?.[key]);
  }, [kind, detail.templates, initialTemplateKey]);
  const [templateKey, setTemplateKey] = useState<string>(templateKeys[0] ?? "");
  const [message, setMessage] = useState(() => (templateKeys[0] ? mergeReturnTemplate(detail.templates[templateKeys[0]], detail, templateExtras) : ""));
  // The last text we merged. While the box still reads exactly that, a changed extra (the refund
  // amount as the deduction moves) re-merges; once the user has edited it, their words stay.
  const lastMerged = useRef(message);
  const extrasKey = JSON.stringify(templateExtras ?? null);
  const isEbay = detail.channel === "ebay";

  const chooseTemplate = (key: string) => {
    setTemplateKey(key);
    if (key && detail.templates[key]) {
      const merged = mergeReturnTemplate(detail.templates[key], detail, templateExtras);
      lastMerged.current = merged;
      setMessage(merged);
    }
  };

  useEffect(() => {
    const body = templateKey ? detail.templates?.[templateKey] : undefined;
    if (!body) return;
    const merged = mergeReturnTemplate(body, detail, templateExtras);
    if (merged === lastMerged.current || message !== lastMerged.current) return;
    lastMerged.current = merged;
    setMessage(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- templateExtras is a fresh object each render; extrasKey compares it by value
  }, [extrasKey, templateKey, detail, message]);

  const ready = canConfirm && (!messageRequired || message.trim().length > 0);

  return (
    <div className="mx-4 mb-4 overflow-hidden rounded-[10px] border border-primary-600">
      <div className="border-b border-gray-200 bg-primary-50 px-3.5 py-2.5 text-[13px] font-semibold text-gray-900">{title ?? actionLabel(kind)}</div>
      <div className="grid gap-3.5 p-3.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))" }}>
        <div className="flex flex-col gap-2.5">
          {children}
          <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-[12px] text-gray-700">
            <span className="font-semibold text-gray-900">{isEbay ? t("What happens next on eBay") : t("What happens next")}</span>
            <span>{whatHappensNext(kind, detail)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={`msg-${kind}`} className="text-[12px] font-semibold text-gray-700">
              {t("Message to the buyer")} <span className="font-normal text-gray-500">· {messageHint ?? (isEbay ? t("eBay comment field") : t("sent with the response"))}</span>
            </label>
            {templateKeys.length > 0 && (
              <select
                value={templateKey}
                onChange={(e) => chooseTemplate(e.target.value)}
                aria-label={t("Template")}
                className="rounded-md border border-gray-200 bg-white py-0.5 pl-2 pr-7 text-[11px] text-gray-700 focus:border-primary-500 focus:ring-primary-500"
              >
                {templateKeys.map((key) => (
                  <option key={key} value={key}>
                    {t("Template: {{name}}", { name: templateLabel(key) })}
                  </option>
                ))}
              </select>
            )}
          </div>
          <textarea
            id={`msg-${kind}`}
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-[13px] leading-relaxed text-gray-900 focus:border-primary-500 focus:ring-primary-500"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onConfirm(message.trim())}
              disabled={busy || !ready}
              className={classNames("rounded-lg px-3.5 py-2 text-[13px] font-semibold text-white", kind === "decline" || kind === "escalate" ? "bg-red-700 hover:bg-red-800" : "bg-primary-600 hover:bg-primary-700", "disabled:opacity-50")}
            >
              {busy ? t("Sending…") : confirmLabel ?? (isEbay ? t("Confirm – send to eBay") : t("Confirm"))}
            </button>
            <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {t("Cancel")}
            </button>
            <span className="ml-auto text-[11px] text-gray-500">{t("Nothing is sent until you confirm.")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
